import {GameState, GameStateType, GameStateStatus, createGameState} from '../models/GameState';
import {GameData} from '../models/GameData';
import databaseService from './databaseService';
import apiService from './apiService';
import getSupabaseClient from './supabaseService';
import {GameLogicService} from './gameLogicService';

const MAX_SLOTS = 3;

class GameStateService {
  private getCurrentUserId = async (): Promise<string> => {
    const supabase = getSupabaseClient();
    const {data: {session}} = await supabase.auth.getSession();
    
    if (!session?.user) {
      throw new Error('User not authenticated');
    }
    
    return session.user.id;
  };

  async createGameState(
    uniqueURI: string,
    slot: number,
    data: GameData,
  ): Promise<GameState> {
    if (slot < 1 || slot > MAX_SLOTS) {
      throw new Error(`Slot must be between 1 and ${MAX_SLOTS}`);
    }

    const userId = await this.getCurrentUserId();
    const gameState = createGameState(uniqueURI, slot, data, userId);

    await databaseService.saveGameState(gameState);
    return gameState;
  }

  async updateGameState(gameState: GameState): Promise<GameState> {
    const isTestMode = gameState.userId === 'test-user' || 
                      (gameState.idApi && gameState.idApi.startsWith('test-'));

    // EN MODE TEST : Ne JAMAIS sauvegarder en DB, juste retourner le state modifié
    // Comme sur Flutter, les gameStates de test restent uniquement en mémoire
    if (isTestMode) {
      console.log(
        `[GameStateService] Test gameState detected (userId=${gameState.userId}), NOT saving to DB - keeping in memory only`,
      );
      const updatedState: GameState = {
        ...gameState,
        dirty: false, // Ne pas marquer comme dirty pour éviter la sync
        lastUpdate: new Date().toISOString(),
      };
      return updatedState;
    }

    if (!isTestMode) {
      const userId = await this.getCurrentUserId();
      // Si le gameState n'a pas de userId ou a 'temp-user', l'assigner automatiquement
      if (!gameState.userId || gameState.userId === 'temp-user') {
        if (gameState.userId === 'temp-user') {
          console.warn('[GameStateService] GameState has temp-user, replacing with current user');
        } else {
          console.warn('[GameStateService] GameState missing userId, assigning current user');
        }
        gameState.userId = userId;
      } else if (gameState.userId !== userId) {
        console.error(`[GameStateService] UserId mismatch: gameState.userId=${gameState.userId}, currentUserId=${userId}`);
        throw new Error('Cannot update game state of another user');
      }
    }

    const updatedState: GameState = {
      ...gameState,
      dirty: true,
      lastUpdate: new Date().toISOString(),
    };

    console.log(`[GameStateService] Updating gameState - skills: ${updatedState.data.skills?.length || 0}, butterflyEffects: ${updatedState.data.butterflyEffects?.length || 0}, objects: ${updatedState.data.objects?.length || 0}`);
    await databaseService.saveGameState(updatedState);
    return updatedState;
  }

  async getGameStateByAdventureAndSlot(
    uniqueURI: string,
    slot: number,
  ): Promise<GameState | null> {
    const state = await databaseService.getGameState(uniqueURI, slot);
    if (!state) return null;

    // Migrer les données notebook si nécessaire (au chargement depuis la DB)
    GameLogicService.migrateNotebookData(state);

    const isTestMode = state.userId === 'test-user' || 
                      (state.idApi && state.idApi.startsWith('test-'));

    if (!isTestMode) {
      const userId = await this.getCurrentUserId();
      if (state.userId !== userId) {
        return null;
      }
    }

    // Vérifier si un backup existe
    const backup = await databaseService.getBackup(uniqueURI, slot);
    const hasBackup = backup !== null;

    if (hasBackup !== state.hasBackup) {
      const updatedState: GameState = {
        ...state,
        hasBackup,
      };
      await databaseService.saveGameState(updatedState);
      return updatedState;
    }

    return state;
  }

  /**
   * Récupère le backup pour un gameState donné
   * Gère à la fois le mode test (backup en mémoire dans gameState.backup) et le mode normal (backup en DB)
   */
  async getBackup(gameState: GameState): Promise<GameState | null> {
    const isTestMode = gameState.userId === 'test-user' || 
                      (gameState.idApi && gameState.idApi.startsWith('test-'));

    if (isTestMode) {
      // Mode test : utiliser le backup en mémoire
      if (gameState.backup) {
        console.log('[GameStateService] Using in-memory backup (test mode)');
        return gameState.backup;
      }
      return null;
    } else {
      // Mode normal : chercher le backup dans la DB
      return await databaseService.getBackup(gameState.uniqueURI, gameState.slot);
    }
  }

  async getAllGameStates(): Promise<GameState[]> {
    const userId = await this.getCurrentUserId();
    const allStates = await databaseService.getAllAdventures();
    return allStates.filter((state: GameState) => state.userId === userId);
  }

  async getGameStatesByAdventure(uniqueURI: string): Promise<GameState[]> {
    const userId = await this.getCurrentUserId();
    const allStates = await databaseService.getAllAdventures();
    return allStates.filter(
      (state: GameState) => state.userId === userId && state.uniqueURI === uniqueURI,
    );
  }

  async deleteGameState(uniqueURI: string, slot: number): Promise<void> {
    const state = await this.getGameStateByAdventureAndSlot(uniqueURI, slot);
    if (!state) {
      throw new Error('Game state not found');
    }

    await databaseService.deleteGameState(uniqueURI, slot);
  }

  /**
   * Synchronise tous les gameStates avec le serveur
   * Identique à Flutter GameStateService.syncAll
   */
  async syncAll(): Promise<void> {
    const userId = await this.getCurrentUserId();
    const lastSyncStr = await databaseService.getLastSync();
    const lastSync = lastSyncStr 
      ? new Date(lastSyncStr).toISOString() 
      : new Date(0).toISOString();
    
    console.log(`[GameStateService] Starting sync - lastSync: ${lastSync}`);
    
    // Récupérer les états serveur et locaux
    const serverStates = await apiService.getGameStates(lastSync);
    const localStates = await databaseService.getDirtyGameStates();
    const deletedSlots = await databaseService.getDeletedSlots();

    console.log(`[GameStateService] Raw local states count: ${localStates.length}`);
    localStates.forEach((state) => {
      console.log(
        `[GameStateService] Local state: ${state.uniqueURI} slot ${state.slot}, userId=${state.userId}, idApi=${state.idApi}, dirty=${state.dirty}`,
      );
    });

    // Filtrer pour ne garder que ceux de l'utilisateur courant
    // EXCLURE TOUS les gameStates de test (userId='test-user' ou idApi commence par 'test-')
    // comme sur Flutter - en mode test, les gameStates ne sont JAMAIS synchronisés avec le serveur
    const userServerStates = serverStates.filter(
      (state) => {
        // Exclure les gameStates de test
        if (state.userId === 'test-user' || state.idApi?.startsWith('test-')) {
          console.log(`[GameStateService] Excluding test server state: ${state.uniqueURI} slot ${state.slot}`);
          return false;
        }
        // Garder uniquement ceux de l'utilisateur courant
        return state.userId === userId;
      }
    );
    const userLocalStates = localStates.filter(
      (state) => {
        // Exclure les gameStates de test AVANT de vérifier le userId
        if (state.userId === 'test-user' || state.idApi?.startsWith('test-')) {
          console.log(`[GameStateService] Excluding test local state: ${state.uniqueURI} slot ${state.slot}, userId=${state.userId}`);
          return false;
        }
        // Garder uniquement ceux de l'utilisateur courant
        const keep = state.userId === userId;
        if (!keep) {
          console.log(`[GameStateService] Excluding local state (wrong userId): ${state.uniqueURI} slot ${state.slot}, userId=${state.userId} (current: ${userId})`);
        }
        return keep;
      }
    );

    console.log(`[GameStateService] Filtered local states count: ${userLocalStates.length} (from ${localStates.length})`);

    // 1. Créer un map des états par aventure et slot
    const serverStatesByAdventure: Record<string, Record<number, GameState>> = {};
    const localStatesByAdventure: Record<string, Record<number, GameState>> = {};
    const deletedSlotsByAdventure: Record<string, Record<number, string>> = {};

    // Organiser les états serveur
    for (const state of userServerStates) {
      // Migrer les données notebook si nécessaire
      GameLogicService.migrateNotebookData(state);
      if (!serverStatesByAdventure[state.uniqueURI]) {
        serverStatesByAdventure[state.uniqueURI] = {};
      }
      serverStatesByAdventure[state.uniqueURI][state.slot] = state;
    }

    // Organiser les états locaux
    for (const state of userLocalStates) {
      // Migrer les données notebook si nécessaire
      GameLogicService.migrateNotebookData(state);
      if (!localStatesByAdventure[state.uniqueURI]) {
        localStatesByAdventure[state.uniqueURI] = {};
      }
      localStatesByAdventure[state.uniqueURI][state.slot] = state;
    }

    // Organiser les slots supprimés
    for (const deleted of deletedSlots) {
      if (!deletedSlotsByAdventure[deleted.uniqueURI]) {
        deletedSlotsByAdventure[deleted.uniqueURI] = {};
      }
      deletedSlotsByAdventure[deleted.uniqueURI][deleted.slot] = deleted.deletedAt;
    }

    // 2. Synchroniser chaque aventure
    const allUniqueURIs = new Set([
      ...Object.keys(serverStatesByAdventure),
      ...Object.keys(localStatesByAdventure),
    ]);

    for (const uniqueURI of allUniqueURIs) {
      const serverSlots = serverStatesByAdventure[uniqueURI] || {};
      const localSlots = localStatesByAdventure[uniqueURI] || {};
      const deletedSlotsForAdventure = deletedSlotsByAdventure[uniqueURI] || {};

      // 2.1 Gérer les slots qui existent des deux côtés
      const allSlots = new Set([
        ...Object.keys(serverSlots).map(Number),
        ...Object.keys(localSlots).map(Number),
      ]);

      for (const slot of allSlots) {
        const serverState = serverSlots[slot];
        const localState = localSlots[slot];
        const deletedAt = deletedSlotsForAdventure[slot];

        // Si le slot a été supprimé localement
        if (deletedAt) {
          // Si le slot existe côté serveur
          if (serverState) {
            // Si la suppression locale est plus récente que la dernière mise à jour du serveur
            if (new Date(deletedAt) > new Date(serverState.lastUpdate)) {
              try {
                // Supprimer le slot du serveur
                if (serverState.idApi) {
                  await apiService.deleteGameState(serverState.idApi);
                }
              } catch (e) {
                console.error(`[GameStateService] Error deleting state ${uniqueURI} slot ${slot} from server:`, e);
              }
            } else {
              // La version serveur est plus récente, on la récupère
              await databaseService.saveGameState(serverState);
              // On efface la suppression locale
              await databaseService.clearDeletedSlot(uniqueURI, slot);
            }
          }
          continue;
        }

        if (serverState && localState) {
          // Les deux états existent, comparer les timestamps
          if (localState.dirty) {
            if (new Date(localState.lastUpdate) > new Date(serverState.lastUpdate)) {
              // État local plus récent
              try {
                const updatedState = await apiService.updateGameState(localState);
                await databaseService.saveGameState({
                  ...updatedState,
                  dirty: false,
                });
              } catch (e) {
                console.error(`[GameStateService] Error syncing state ${uniqueURI} slot ${slot}:`, e);
              }
            } else {
              // État serveur plus récent
              await databaseService.saveGameState(serverState);
            }
          } else {
            // Utiliser l'état serveur
            await databaseService.saveGameState(serverState);
          }
        } else if (serverState) {
          // L'état n'existe que côté serveur
          await databaseService.saveGameState(serverState);
        } else if (localState && localState.dirty) {
          // VÉRIFICATION FINALE : Ne JAMAIS synchroniser les gameStates de test
          if (localState.userId === 'test-user' || localState.idApi?.startsWith('test-')) {
            console.log(
              `[GameStateService] Skipping sync for test gameState: ${uniqueURI} slot ${slot} (userId=${localState.userId}, idApi=${localState.idApi})`,
            );
            continue;
          }
          
          // L'état n'existe que côté local et est modifié
          // Identique à Flutter: POST vers serveur pour créer
          try {
            const createdState = await apiService.createGameState(localState);
            // Si succès, sauvegarder avec dirty=false
            await databaseService.saveGameState({
              ...createdState,
              dirty: false,
            });
          } catch (e: any) {
            // Si erreur, logger et continuer (le gameState reste dirty)
            // Identique à Flutter: on continue même si la création échoue
            console.error(`[GameStateService] Error creating state ${uniqueURI} slot ${slot}:`, e);
          }
        }
      }

      // 2.2 Vérifier la cohérence des slots
      const allSlotsSet = new Set([
        ...Object.keys(serverSlots).map(Number),
        ...Object.keys(localSlots).map(Number),
      ]);
      if (allSlotsSet.size > MAX_SLOTS) {
        console.warn(`[GameStateService] More than ${MAX_SLOTS} slots found for adventure ${uniqueURI}`);
        // Supprimer les slots en trop
        const sortedSlots = Array.from(allSlotsSet).sort((a, b) => a - b);
        for (let i = MAX_SLOTS; i < sortedSlots.length; i++) {
          const slotToDelete = sortedSlots[i];
          if (localSlots[slotToDelete]) {
            await this.deleteGameState(uniqueURI, slotToDelete);
          }
        }
      }
    }

    // 3. Nettoyer les slots supprimés après une synchronisation réussie
    await databaseService.clearDeletedSlots();
    await databaseService.updateLastSync(new Date().toISOString());
    
    console.log(`[GameStateService] Sync completed successfully`);
  }
}

export default new GameStateService();
